// generateTimeSheetPDF.js
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
const UnitechHeader = "../img/quotation_header.png";
const UnitechFooter = "../img/cover_letter_footer.png";

let config = {
  margin: 25,
  fontSize: 10.5,
  headerHeight: 90,
  footerHeight: 120,
};

function addPageHeader(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  doc.addImage(
    UnitechHeader,
    "PNG",
    config.margin,
    10,
    pageWidth - config.margin * 2,
    config.headerHeight - 20
  );
}

function addPageFooter(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // ── FOOTER IMAGE ──
  const footerImgY = pageHeight - 40;
  doc.addImage(
    UnitechFooter,
    "PNG",
    config.margin,
    footerImgY,
    pageWidth - config.margin * 2,
    30
  );

  // ── SIGNATURE SECTION (above footer image) ──
  const sigY = pageHeight - 105;

  doc.setFont("Times", "bold");
  doc.setFontSize(9);
  doc.text("Prepared By", config.margin, sigY);
  doc.text("Checked By", pageWidth / 2 - 30, sigY);
  doc.text("Approved By", pageWidth - config.margin - 80, sigY);

  const sigY2 = sigY + 18;
  doc.setFont("Times", "normal");
  doc.setFontSize(9);
  doc.text("Sign: ______________", config.margin, sigY2);
  doc.text("Sign: ______________", pageWidth / 2 - 30, sigY2);
  doc.text("Sign: ______________", pageWidth - config.margin - 100, sigY2);

  const sigY3 = sigY2 + 15;
  doc.text("Name: ______________", config.margin, sigY3);
  doc.text("Name: ______________", pageWidth / 2 - 30, sigY3);
  doc.text("Name: ______________", pageWidth - config.margin - 100, sigY3);

  const sigY4 = sigY3 + 15;
  doc.text("Date: ________________", config.margin, sigY4);
  doc.text("Date: ________________", pageWidth / 2 - 30, sigY4);
  doc.text("Date: ________________", pageWidth - config.margin - 100, sigY4);
}

function addHeadersAndFooters(doc, engineerName = "") {
  const totalPages = doc.getNumberOfPages();
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);
    const pageWidth = doc.internal.pageSize.getWidth();

    // ── HEADER IMAGE ──
    addPageHeader(doc);
    // ── MAIN HEADING (center) — every page ──
    const headingY = config.headerHeight + 5;
    doc.setFont("Times", "bold");
    doc.setFontSize(16);
    const mainTitle = "Expense And Time Sheet";
    const titleWidth = doc.getTextWidth(mainTitle);
    doc.text(mainTitle, (pageWidth - titleWidth) / 2, headingY);

    // ── PAGE NO (left) + ENGINEER NAME (right) — same line below heading ──
    const infoY = headingY + 15;
    doc.setFont("Times", "bold");
    doc.setFontSize(11);
    let pageTxt = `Page No : ${String(page).padStart(2, "0")} of ${String(totalPages).padStart(2, "0")}`;
    doc.text(pageTxt, config.margin, infoY);
    doc.text(`Name of Engineer: ${engineerName}`, pageWidth - config.margin, infoY, { align: "right" });

    // ── UNDERLINE — every page ──
    const lineY = infoY + 8;
    doc.setLineWidth(1);
    doc.line(config.margin, lineY, pageWidth - config.margin, lineY);

    // ── FOOTER ──
    addPageFooter(doc);
  }
}

export async function generateTimeSheetPDF(data, engineerName = "") {
  const dataList = Array.isArray(data) ? data : [data];

  const doc = new jsPDF({
    orientation: "p",
    unit: "pt",
    format: "a4",
    compress: true,
  });

  const pageWidth = doc.internal.pageSize.getWidth();

  // startY must account for heading + info line + underline
  const tableStartY = config.headerHeight + 40;

  // ── TABLE COLUMNS ──
  const columns = [
    { header: "Sr.\nNo.", dataKey: "srNo" },
    { header: "Client/Project", dataKey: "clientProject" },
    { header: "Visit\nDate", dataKey: "visitDate" },
    { header: "Vendor\nDetails", dataKey: "vendorDetails" },
    { header: "Travel\nFrom", dataKey: "travelFrom" },
    { header: "Travel\nTo", dataKey: "travelTo" },
    { header: "Mode of\nTravel", dataKey: "modeOfTravel" },
    { header: "Distance\n(Km)", dataKey: "distanceKm" },
    { header: "In\nTime", dataKey: "inTime" },
    { header: "Out\nTime", dataKey: "outTime" },
    { header: "Total\nHours", dataKey: "totalHours" },
    { header: "Expend", dataKey: "expenditureInr" },
    { header: "Remarks", dataKey: "remarks" },
  ];

  // ── TABLE ROWS ──
  const rows = dataList.map((item, index) => ({
    srNo: index + 1,
    clientProject: item.clientProject || "-",
    visitDate: item.visitDate || "-",
    vendorDetails: item.vendorDetails || "-",
    travelFrom: item.travelFrom || "-",
    travelTo: item.travelTo || "-",
    modeOfTravel: item.modeOfTravel || "-",
    distanceKm: item.distanceKm ?? "-",
    inTime: item.inTime || "-",
    outTime: item.outTime || "-",
    totalHours: item.totalHours ?? "-",
    expenditureInr: item.expenditureInr ?? "-",
    remarks: item.remarks || "-",
  }));

  autoTable(doc, {
    startY: tableStartY,
    columns: columns,
    body: rows,
    margin: {
      top: config.headerHeight + 40,
      left: config.margin,
      right: config.margin,
      bottom: config.footerHeight + 10,
    },
    theme: "grid",
    styles: {
      font: "Times",
      fontSize: 7,
      cellPadding: 4,
      valign: "middle",
      halign: "center",
      lineColor: [0, 0, 0],
      lineWidth: 0.5,
      textColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: false,
      textColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize: 7.5,
      halign: "center",
      valign: "middle",
    },
    columnStyles: {
      0:  { cellWidth: 22 },
      1:  { cellWidth: 60 },
      2:  { cellWidth: 42 },
      3:  { cellWidth: 88 },
      4:  { cellWidth: 38 },
      5:  { cellWidth: 38 },
      6:  { cellWidth: 38 },
      7:  { cellWidth: 35 },
      8:  { cellWidth: 28 },
      9:  { cellWidth: 28 },
      10: { cellWidth: 35 },
      11: { cellWidth: 42 },
      12: { cellWidth: 53 },
    },
    didParseCell: (data) => {
      data.cell.styles.overflow = "linebreak";
    },
  });

  // ── HEADERS & FOOTERS ON ALL PAGES ──
  addHeadersAndFooters(doc, engineerName);

  doc.save(`TimeSheet_${engineerName || "Report"}.pdf`);
}